import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Check, ChevronDown, Clock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@workspace/ui/components/ui/popover";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerNestedRoot, DrawerTitle } from "@workspace/ui/components/ui/drawer";
import { useInsideVaulDrawer } from "@/lib/drawer-nesting";
import { useIsMobile } from "@/hooks/use-mobile";
import { parseBookingTime } from "@/lib/booking-time";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);
const PERIODS = ["am", "pm"] as const;
type Period = (typeof PERIODS)[number];
type Parts = { hour: number; minute: number; period: Period };

function formatTimeString(hour: number, minute: number, period: string): string {
  return `${hour}:${minute.toString().padStart(2, "0")} ${period}`;
}

function partsFrom(value: string): Parts {
  const parsed = parseBookingTime(value);
  return parsed
    ? { hour: parsed.hour12, minute: parsed.minute, period: parsed.period }
    : { hour: 8, minute: 0, period: "am" };
}

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  size?: "sm" | "lg";
  /** Fired once after Done commits a selection (never on cancel/dismiss). */
  onCommit?: () => void;
  /** Context announced on the trigger and dialog, e.g. Pick-up or Drop-off. */
  label?: string;
  disabled?: boolean;
}

/**
 * Single-trigger time picker. The saved value stays fully controlled by the
 * parent (Task #2655); the open picker edits a local draft that is only
 * pushed through `onChange` when the user taps Done. Cancel, Escape, outside
 * click and swipe-dismiss all discard the draft. Mobile (<768px) uses a
 * bottom sheet; desktop uses a compact popover.
 */
export function TimePicker({ value, onChange, size = "sm", onCommit, label, disabled }: TimePickerProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const nested = useInsideVaulDrawer();
  const context = label ?? t("booking.time", "Time");
  const current = partsFrom(value);
  const display = `${current.hour}:${current.minute.toString().padStart(2, "0")} ${current.period.toUpperCase()}`;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Parts>(current);

  const handleOpenChange = (next: boolean) => {
    // Re-seed the draft from the latest controlled value every time we open.
    if (next) setDraft(partsFrom(value));
    setOpen(next);
  };

  const handleDone = () => {
    const next = formatTimeString(draft.hour, draft.minute, draft.period);
    const changed = draft.hour !== current.hour || draft.minute !== current.minute || draft.period !== current.period;
    setOpen(false);
    if (changed) onChange(next);
    // Defer so parent callbacks that close over derived state (e.g.
    // BookingWidget's time-range validation) see the committed value,
    // mirroring the old change → re-render → blur ordering.
    if (onCommit) setTimeout(onCommit, 0);
  };

  const isLg = size === "lg";
  const trigger = (
    <button
      type="button"
      disabled={disabled}
      aria-label={`${context}: ${display}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-testid="time-picker-trigger"
      data-value={formatTimeString(current.hour, current.minute, current.period)}
      className={cn(
        "group flex w-full min-h-11 min-w-max items-center gap-2 rounded-md border border-border bg-background text-left text-foreground transition-colors",
        "hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-60",
        open && "border-primary ring-1 ring-primary",
        isLg ? "px-3.5 py-3 text-base" : "px-3 py-2 text-base md:text-sm",
      )}
    >
      <Clock aria-hidden="true" className={cn("shrink-0 text-muted-foreground", isLg ? "h-5 w-5" : "h-4 w-4")} />
      <span className="flex-1 font-medium tabular-nums">{display}</span>
      <ChevronDown aria-hidden="true" className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
    </button>
  );

  const body = (
    <TimeGrid
      context={context}
      draft={draft}
      setDraft={setDraft}
      onCancel={() => setOpen(false)}
      onDone={handleDone}
      mobile={isMobile}
      header={
        <div className={cn(
          "flex items-baseline justify-between gap-3",
          // The mobile Sheet owns a 44px close control at the right edge.
          // Reserve its column instead of allowing it to cover the preview.
          isMobile && "min-h-11 items-center pr-14",
        )}>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{context}</span>
          <span data-testid="time-picker-preview" className="text-2xl font-semibold tabular-nums text-foreground">
            {draft.hour}:{draft.minute.toString().padStart(2, "0")}
            <span className="ml-1 text-base font-medium text-muted-foreground">{draft.period.toUpperCase()}</span>
          </span>
        </div>
      }
    />
  );

  if (isMobile) {
    const Root = nested ? DrawerNestedRoot : Drawer;
    return (
      <>
        <div onClick={() => !disabled && handleOpenChange(true)}>{trigger}</div>
        {/* Vaul drawer: the whole sheet is draggable; releasing past the
            threshold calls onOpenChange(false), which discards the draft. */}
        <Root open={open} onOpenChange={handleOpenChange} repositionInputs={false}>
          <DrawerContent
            data-testid="time-picker-sheet"
            data-nested={nested ? "true" : undefined}
            className="z-[70] mt-0 max-h-[90dvh] rounded-t-2xl px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
          >
            <DrawerTitle className="sr-only">{context}</DrawerTitle>
            <DrawerDescription className="sr-only">{t("booking.time_choose", "Choose a time")}</DrawerDescription>
            <div className="relative mt-3 overflow-y-auto overscroll-contain">
              {body}
              <DrawerClose
                data-testid="time-picker-close"
                aria-label={t("common.close", "Close")}
                className="absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center rounded-full bg-muted/70 text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </DrawerClose>
            </div>
          </DrawerContent>
        </Root>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        role="dialog"
        aria-label={context}
        data-testid="time-picker-popover"
        className="z-[70] pointer-events-auto w-[300px] rounded-xl border border-border bg-popover p-3 shadow-xl"
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}

interface TimeGridProps {
  context: string;
  draft: Parts;
  setDraft: (updater: (p: Parts) => Parts) => void;
  onCancel: () => void;
  onDone: () => void;
  mobile: boolean;
  header: ReactNode;
}

function TimeGrid({ context, draft, setDraft, onCancel, onDone, mobile, header }: TimeGridProps) {
  const { t } = useTranslation();
  const cell = cn(
    "flex items-center justify-center rounded-md border text-base tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    mobile ? "min-h-12" : "min-h-10 md:text-sm",
  );
  const on = "border-foreground bg-foreground font-semibold text-background";
  const off = "border-border bg-background text-foreground hover:bg-muted";
  const handleRadioKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End", " "].includes(event.key)) return;
    const radios = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button[role="radio"]:not(:disabled)'));
    const index = radios.indexOf(event.target as HTMLButtonElement);
    if (index < 0) return;
    event.preventDefault();
    event.stopPropagation();
    const next = event.key === "Home" ? 0
      : event.key === "End" ? radios.length - 1
      : event.key === " " ? index
      : (index + (["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1) + radios.length) % radios.length;
    radios[next].focus({ preventScroll: true });
    radios[next].click();
  };
  // Retained historical times may have an off-grid minute. Keep that value
  // untouched, but still provide one keyboard entry into the minute group.
  const minuteTabStop = MINUTES.includes(draft.minute) ? draft.minute : MINUTES[0];

  return (
    <div className="space-y-3">
      {header}
      <fieldset>
        <legend className="mb-1.5 text-xs font-medium text-muted-foreground">{t("booking.time_hour", "Hour")}</legend>
        <div role="radiogroup" aria-label={`${context} — ${t("booking.time_hour", "Hour")}`} onKeyDown={handleRadioKeys} className="grid grid-cols-6 gap-1.5">
          {HOURS.map(h => (
            <button key={h} type="button" role="radio" aria-checked={draft.hour === h} data-testid={`time-hour-${h}`}
              tabIndex={draft.hour === h ? 0 : -1}
              onClick={() => setDraft(p => ({ ...p, hour: h }))} className={cn(cell, draft.hour === h ? on : off)}>
              {h}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-1.5 text-xs font-medium text-muted-foreground">{t("booking.time_minute", "Minute")}</legend>
        <div role="radiogroup" aria-label={`${context} — ${t("booking.time_minute", "Minute")}`} onKeyDown={handleRadioKeys} className="grid grid-cols-6 gap-1.5">
          {MINUTES.map(m => (
            <button key={m} type="button" role="radio" aria-checked={draft.minute === m} data-testid={`time-minute-${m}`}
              tabIndex={minuteTabStop === m ? 0 : -1}
              onClick={() => setDraft(p => ({ ...p, minute: m }))} className={cn(cell, draft.minute === m ? on : off)}>
              :{m.toString().padStart(2, "0")}
            </button>
          ))}
        </div>
      </fieldset>
      <div role="radiogroup" aria-label={`${context} — ${t("booking.time_period", "AM/PM")}`} onKeyDown={handleRadioKeys} className="grid grid-cols-2 gap-1.5 rounded-lg bg-muted p-1">
        {PERIODS.map(p => (
          <button key={p} type="button" role="radio" aria-checked={draft.period === p} data-testid={`time-period-${p}`}
            tabIndex={draft.period === p ? 0 : -1}
            onClick={() => setDraft(d => ({ ...d, period: p }))}
            className={cn("rounded-md font-medium uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              mobile ? "min-h-12 text-base" : "min-h-10 text-sm",
              draft.period === p ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>
            {p.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} data-testid="time-picker-cancel"
          className={cn("flex-1 rounded-md border border-border font-medium hover:bg-muted", mobile ? "min-h-12 text-base" : "min-h-10 text-sm")}>
          {t("common.cancel", "Cancel")}
        </button>
        <button type="button" onClick={onDone} data-testid="time-picker-done"
          className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-md bg-foreground font-semibold text-background hover:bg-foreground/90", mobile ? "min-h-12 text-base" : "min-h-10 text-sm")}>
          <Check aria-hidden="true" className="h-4 w-4" />
          {t("common.done", "Done")}
        </button>
      </div>
    </div>
  );
}
